import axios from 'axios'
import { i18n } from 'i18next'
import {
  every,
  forEach,
  get,
  isEmpty,
  isObject,
  keys,
  map,
  mapKeys,
  merge,
  set,
  toLower,
} from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useLocalStorage } from '../../hooks'
import { request } from '../../utils'
import LoaderOverlay from '../LoaderOverlay'
import { TranslationContext, useTranslation } from './helpers'
import { requestTranslations } from './requests'

let translationCache

type TTranslationContextProvider = {
  projects?: string[]
  tenantId?: number
  children?: React.ReactNode
  i18n?: any
  initialLanguage?: string
  skipSaveLanguage?: boolean
  allowedLanguages?: string[]
  useWiwo?: boolean
  languageFiles?: string[]
  i18nInstancesToSync?: i18n[]
}

export const TranslationContextProvider = (
  props: TTranslationContextProvider,
): React.ReactElement => {
  const {
    projects,
    tenantId,
    children,
    i18n,
    initialLanguage,
    skipSaveLanguage = false,
    useWiwo = false,
    languageFiles = [],
    i18nInstancesToSync = [],
  } = props

  const allowedLanguages = useMemo(
    () => map(props.allowedLanguages, (l) => toLower(l)),
    [props.allowedLanguages],
  )

  const [languages, setLanguages] = useState<string[]>([])

  const [isLoading, setIsLoading] = useState(true)

  const loaded = useRef({})

  const [language, setLanguage] = useState(i18n.language)

  const [translationHash, setTranslationHash] = useLocalStorage<
    string | undefined
  >('translationHash')

  useEffect(() => {
    moment.locale(language)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    global.locale = language
  }, [language])

  const saveLanguage = async (value: string): Promise<void> => {
    try {
      if (value === initialLanguage) {
        return
      }
      await request({
        method: 'post',
        url: '/api/profile/language',
        data: { language: value },
      })

      if (get(global, 'currentUser.user.language')) {
        set(global, 'currentUser.user.language', value)
      }

      if (get(global, 'currentUser.language')) {
        set(global, 'currentUser.language', value)
      }
    } catch (err) {
      console.warn('Failed to save language on server', err)
    }
  }

  const onLanguageChanged = (lng: string): void => {
    i18nInstancesToSync.forEach((instance) => {
      instance.changeLanguage(lng)
    })
    setLanguage(lng)

    if (!skipSaveLanguage) {
      saveLanguage(lng)
    }
  }

  const completeLoading = (): void => {
    setIsLoading(false)
    if (initialLanguage && initialLanguage !== i18n.language) {
      i18n.changeLanguage(initialLanguage)
    }
  }

  // called by i18n when a resource bundle is added
  const onResourcesAdded = (lng, ns): void => {
    // set flag to cache that its handled
    if (!get(loaded.current, lng)) {
      set(loaded.current, lng, true)
    }
    // check if this was the last handled bundle
    if (every(keys(translationCache), (key) => get(loaded.current, key))) {
      // clear previous language data for these languages
      forEach(allowedLanguages, (lng) => i18n.removeResourceBundle(lng))
      const languagesToLoad = isEmpty(allowedLanguages)
        ? keys(translationCache)
        : allowedLanguages
      setLanguages(languagesToLoad)
      i18n.loadLanguages(languagesToLoad, (err) => {
        completeLoading()
      })
    }
  }

  const [hasLoaded, setHasLoaded] = useState(false)
  const init = async (): Promise<void> => {
    try {
      // set initial translationCache
      try {
        const translationCacheStorage =
          window.localStorage.getItem('translationCache')
        if (translationCacheStorage) {
          translationCache = JSON.parse(translationCacheStorage)
        }
      } catch (err) {
        // local storage loading failed, ignore
      }

      i18n.store.on('added', onResourcesAdded)
      i18n.on('languageChanged', onLanguageChanged)

      setIsLoading(true)

      // Download translation files from public folder
      if (useWiwo) {
        if (i18n) {
          const allTranslations = {}
          for (const languageFile of languageFiles) {
            const { data: downloadedTranslations } = await axios.get(
              `/translations/${languageFile}`,
              { headers: { 'Accept-Language': i18n?.language ?? 'de' } },
            )
            merge(allTranslations, downloadedTranslations)
          }

          translationCache = {
            de: {},
            ...mapKeys(allTranslations, (v, k) => {
              return k === 'en-us' || k === 'en-gb' ? 'en' : k
            }),
          }
          // console.log('translationCache: ', translationCache)
          if (!isEmpty(translationCache)) {
            forEach(translationCache, (resources, language) => {
              // console.log('Adding resource', { language, resources })
              i18n.addResourceBundle(
                language,
                'translation',
                resources,
                true,
                true,
              )
            })
          } else {
            completeLoading()
          }
        }
        return
      }

      const { data, headers, status } = await requestTranslations(
        {
          projects,
          tenant_id: tenantId,
        },
        translationCache ? (translationHash as string) : '',
      )

      if (i18n) {
        const etag = get(headers, 'etag')
        setTranslationHash(etag)

        if (status === 200 && isObject(data)) {
          translationCache = data
          // store translationCache in localStorage
          try {
            window.localStorage.setItem(
              'translationCache',
              JSON.stringify(data),
            )
          } catch (err) {
            // failed to store in localStorage, stored hash is useless
            setTranslationHash(undefined)
          }
        }
        if (!isEmpty(translationCache)) {
          forEach(translationCache, (resources, language) => {
            i18n.addResourceBundle(
              language,
              'translation',
              resources,
              true,
              true,
            )
          })
        } else {
          completeLoading()
        }
      }
    } catch (err) {
      console.error('Failed to load translations from server', {
        projects,
        err,
      })
      completeLoading()
      // toast.error("Laden der Übersetzungsdatei fehlgeschlagen.")
    } finally {
      setHasLoaded(true)
    }
  }

  useEffect(() => {
    init()
  }, [])

  return (
    <>
      <LoaderOverlay isLoading={isLoading} fullscreen></LoaderOverlay>
      <TranslationContext.Provider
        value={{ translationHash: translationHash as string, languages }}
      >
        <React.Fragment key={language}>
          {hasLoaded && !isLoading && children}
        </React.Fragment>
      </TranslationContext.Provider>
    </>
  )
}

export { useTranslation }
